import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Archive, ChatCircleDots, UserPlus } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/roles";

const STAGES = ["Prospect", "Toured", "Applied", "Incomplete", "Awaiting Review", "Awaiting Documents",
  "Awaiting Payment", "Awaiting Decision", "Approved", "Denied", "Waitlisted", "Enrolled", "Active", "Inactive", "Archived", "Removed"];
const tone = {
  Prospect: "border-slate-200", Toured: "border-amber-300", Applied: "border-blue-300",
  Waitlisted: "border-purple-300", Enrolled: "border-emerald-300", Inactive: "border-slate-200",
  Approved: "border-emerald-300", Denied: "border-red-300", Archived: "border-slate-200", Removed: "border-red-200",
};

const childAge = (birthday) => {
  if (!birthday) return "—";
  const born = new Date(`${birthday}T12:00:00`); const now = new Date();
  let years = now.getFullYear() - born.getFullYear();
  if (now < new Date(now.getFullYear(), born.getMonth(), born.getDate())) years -= 1;
  return years < 2 ? `${Math.max(0, Math.round((now - born) / 2629800000))} months` : `${years} years`;
};

export default function Admissions() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState(STAGES.includes(params.get("status")) ? params.get("status") : "all");
  const [noteDrafts, setNoteDrafts] = useState({});

  const load = () => api.get("/applications").then(({ data }) => setApps(data));
  useEffect(() => { load(); }, []);

  const move = async (id, status, admissions_note = "") => {
    try {
      await api.patch(`/applications/${id}`, { status, admissions_note });
      toast.success(`Moved to ${status}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const enroll = async (id) => {
    try {
      const { data } = await api.post(`/applications/${id}/enroll`);
      toast.success(`${data.first_name} enrolled and added to the roster`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const shown = apps.filter((a) => (!params.get("id") || a.id === params.get("id")) && (filter === "all" || a.status === filter));

  return (
    <div data-testid="admissions-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Admissions</p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight">Enrollment pipeline</h1>
        </div>
        <Select value={filter} onValueChange={(v) => { setFilter(v); setParams(v === "all" ? {} : { status: v }); }}>
          <SelectTrigger className="h-12 rounded-xl w-52" data-testid="pipeline-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-8 flex flex-wrap gap-3" data-testid="pipeline-counts">
        {STAGES.map((s) => {
          const c = apps.filter((a) => a.status === s).length;
          return (
            <span key={s} className={`text-sm font-bold px-4 py-2 rounded-full bg-white border-2 ${tone[s]}`}>
              {s} · {c}
            </span>
          );
        })}
      </div>

      <div className="mt-6 grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="applications-list">
        {shown.length === 0 && <p className="text-muted-foreground">No applications in this stage.</p>}
        {shown.map((a) => (
          <div key={a.id} className={`bg-white rounded-2xl border-2 p-6 ${tone[a.status] || "border-border"}`}
               data-testid={`application-card-${a.id}`}>
            <p className="font-display text-lg font-semibold">{a.child_first_name} {a.child_last_name}</p>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6] mt-1">{a.program}</p>
            <dl className="mt-5 space-y-2 text-sm">
              {[["Age", childAge(a.child_birthday)], ["Parent", a.parent_name], ["Email", a.parent_email], ["Phone", a.parent_phone || "—"],
                ["Submitted", a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"], ["Desired start", a.desired_start || "—"],
                ["Requested program", a.program || "—"], ["Requested schedule", a.requested_schedule || "Not provided"],
                ["Missing information", [["child_birthday", "birth date"], ["parent_phone", "parent phone"], ["address", "address"]].filter(([key]) => !a[key]).map(([, label]) => label).join(", ") || "None"],
                ["Missing documents", (a.missing_documents || []).join(", ") || "None listed"], ["Application fee", "No fee required"],
                ["Assigned reviewer", a.assigned_reviewer || "Unassigned"],
                ["Last activity", a.updated_at ? new Date(a.updated_at).toLocaleString() : a.created_at ? new Date(a.created_at).toLocaleString() : "—"],
                ["Signed", a.signature || "—"]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-muted-foreground font-semibold">{k}</dt>
                  <dd className="font-semibold text-right truncate">{v}</dd>
                </div>
              ))}
            </dl>
            {a.notes && <p className="mt-4 text-sm text-muted-foreground italic">"{a.notes}"</p>}
            <div className="mt-5 space-y-3">
              <Select value={a.status} onValueChange={(v) => move(a.id, v, a.admissions_note || "")}>
                <SelectTrigger className="h-11 rounded-xl" data-testid={`stage-select-${a.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => enroll(a.id)}
                        className="flex-1 rounded-full bg-[#10B981] hover:bg-emerald-600 font-bold"
                        data-testid={`enroll-btn-${a.id}`}>
                  <UserPlus size={16} className="mr-1" /> Enroll
                </Button>
                {isAdmin(user) && (
                  <Button size="sm" variant="outline" onClick={() => move(a.id, "Archived", a.admissions_note || "")}
                          className="rounded-full font-bold text-slate-600" data-testid={`archive-app-${a.id}`}>
                    <Archive size={15} />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button size="sm" variant="outline" onClick={() => move(a.id, "Approved", a.admissions_note || "")}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => move(a.id, "Denied", a.admissions_note || "")}>Deny</Button>
                <Button size="sm" variant="outline" onClick={() => move(a.id, "Waitlisted", a.admissions_note || "")}>Waitlist</Button>
                <Button size="sm" variant="outline" asChild><Link to={`/admin/messages?with=${encodeURIComponent(a.parent_email)}`}><ChatCircleDots className="mr-1" /> Contact</Link></Button>
                <Button size="sm" variant="outline" asChild><Link to={`/admin/messages?with=${encodeURIComponent(a.parent_email)}&draft=${encodeURIComponent(`Hello ${a.parent_name}, we need a little more information to finish reviewing ${a.child_first_name}'s application.`)}`}>Request information</Link></Button>
                <Button size="sm" variant="outline" asChild><Link to={`/admin/messages?with=${encodeURIComponent(a.parent_email)}&draft=${encodeURIComponent(`Hello ${a.parent_name}, please upload the remaining documents for ${a.child_first_name}'s application.`)}`}>Request documents</Link></Button>
              </div>
              <div><Textarea value={noteDrafts[a.id] ?? a.admissions_note ?? ""} onChange={(e) => setNoteDrafts({ ...noteDrafts, [a.id]: e.target.value })} placeholder="Internal admissions note" className="min-h-20" /><Button size="sm" variant="link" className="px-0" onClick={() => move(a.id, a.status, noteDrafts[a.id] ?? a.admissions_note ?? "")}>Save internal note</Button></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
